import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Building, Phone, Mail, Globe, MapPin, CheckCircle, Languages, Accessibility, Plus } from "lucide-react";

interface Provider {
  id: string;
  organization_name: string;
  organization_type: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  primary_phone: string;
  primary_email?: string;
  website?: string;
  latitude?: number;
  longitude?: number;
  distance_miles?: number;
  data_quality_score?: number;
  services: string[];
  languages_spoken: string[];
  accepts_medicaid: boolean;
  accepts_medicare: boolean;
  hours_of_operation?: any;
  wheelchair_accessible?: boolean;
  status: string;
}

interface AddProviderFormProps {
  onSubmit: (provider: Provider) => void;
  onCancel: () => void;
}

// Provider types from sample providers
const PROVIDER_TYPES = [
  "Community-Based Organization",
  "Healthcare Provider",
  "Mental Health Services",
  "Housing Services",
  "Transportation Services",
  "Food Bank",
  "Social Services Agency",
  "Non-Profit Organization",
  "Government Agency",
  "Faith-Based Organization"
];

// Services from sample providers
const AVAILABLE_SERVICES = [
  "Housing Assistance",
  "Food Assistance",
  "Transportation",
  "Mental Health",
  "Substance Abuse Treatment",
  "Employment Services",
  "Case Management",
  "Healthcare Navigation",
  "Benefits Enrollment",
  "Legal Aid",
  "Childcare",
  "Education Services",
  "Financial Counseling",
  "Utility Assistance",
  "Emergency Assistance"
];

// Languages
const LANGUAGES = [
  "English",
  "Spanish",
  "Chinese (Mandarin)",
  "Chinese (Cantonese)",
  "Vietnamese",
  "Korean",
  "Tagalog",
  "Arabic",
  "Russian",
  "French",
  "Portuguese",
  "Other"
];

// US States
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export function AddProviderForm({ onSubmit, onCancel }: AddProviderFormProps) {
  const [formData, setFormData] = useState({
    organization_name: "",
    organization_type: "",
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
    primary_phone: "",
    primary_email: "",
    website: "",
    services: [] as string[],
    languages_spoken: [] as string[],
    accepts_medicaid: false,
    accepts_medicare: false,
    wheelchair_accessible: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleLanguageToggle = (language: string) => {
    setFormData(prev => ({
      ...prev,
      languages_spoken: prev.languages_spoken.includes(language)
        ? prev.languages_spoken.filter(l => l !== language)
        : [...prev.languages_spoken, language]
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.organization_name.trim()) {
      newErrors.organization_name = "Organization name is required";
    }
    if (!formData.organization_type) {
      newErrors.organization_type = "Organization type is required";
    }
    if (!formData.street_address.trim()) {
      newErrors.street_address = "Street address is required";
    }
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }
    if (!formData.state) {
      newErrors.state = "State is required";
    }
    if (!formData.zip_code.trim()) {
      newErrors.zip_code = "ZIP code is required";
    }
    if (!formData.primary_phone.trim()) {
      newErrors.primary_phone = "Phone number is required";
    }
    if (formData.services.length === 0) {
      newErrors.services = "Please select at least one service";
    }
    if (formData.languages_spoken.length === 0) {
      newErrors.languages_spoken = "Please select at least one language";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const newProvider: Provider = {
      id: `manual-${Date.now()}`,
      organization_name: formData.organization_name,
      organization_type: formData.organization_type,
      street_address: formData.street_address,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zip_code,
      primary_phone: formData.primary_phone,
      primary_email: formData.primary_email || undefined,
      website: formData.website || undefined,
      services: formData.services,
      languages_spoken: formData.languages_spoken,
      accepts_medicaid: formData.accepts_medicaid,
      accepts_medicare: formData.accepts_medicare,
      wheelchair_accessible: formData.wheelchair_accessible,
      status: "active",
    };

    onSubmit(newProvider);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <Card className="border border-blue-200/50 shadow-2xl bg-white/95 backdrop-blur-sm rounded-3xl overflow-hidden">
          <CardHeader className="pb-6 bg-gradient-to-r from-green-50/90 to-emerald-50/90 border-b border-green-200/40">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-3 text-2xl font-bold text-gray-800">
                <div className="p-3 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl shadow-xl">
                  <Building className="w-6 h-6 text-white" />
                </div>
                <span>Add New Provider</span>
              </CardTitle>
              <Button
                variant="outline"
                onClick={onCancel}
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                  <Building className="w-5 h-5 text-blue-600" />
                  <span>Basic Information</span>
                </h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="organization_name" className="text-sm font-semibold text-gray-700">
                      Organization Name *
                    </Label>
                    <Input
                      id="organization_name"
                      value={formData.organization_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, organization_name: e.target.value }))}
                      className="mt-1"
                      placeholder="Enter organization name"
                    />
                    {errors.organization_name && (
                      <p className="text-sm text-red-600 mt-1">{errors.organization_name}</p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <Label htmlFor="organization_type" className="text-sm font-semibold text-gray-700">
                      Organization Type *
                    </Label>
                    <Select
                      value={formData.organization_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, organization_type: value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select organization type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROVIDER_TYPES.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.organization_type && (
                      <p className="text-sm text-red-600 mt-1">{errors.organization_type}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                  <Phone className="w-5 h-5 text-green-600" />
                  <span>Contact Information</span>
                </h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="street_address" className="text-sm font-semibold text-gray-700">
                      Street Address *
                    </Label>
                    <Input
                      id="street_address"
                      value={formData.street_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, street_address: e.target.value }))}
                      className="mt-1"
                      placeholder="Enter street address"
                    />
                    {errors.street_address && (
                      <p className="text-sm text-red-600 mt-1">{errors.street_address}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="city" className="text-sm font-semibold text-gray-700">
                      City *
                    </Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="mt-1"
                      placeholder="City"
                    />
                    {errors.city && (
                      <p className="text-sm text-red-600 mt-1">{errors.city}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="state" className="text-sm font-semibold text-gray-700">
                      State *
                    </Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.state && (
                      <p className="text-sm text-red-600 mt-1">{errors.state}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="zip_code" className="text-sm font-semibold text-gray-700">
                      ZIP Code *
                    </Label>
                    <Input
                      id="zip_code"
                      value={formData.zip_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                      className="mt-1"
                      placeholder="ZIP Code"
                    />
                    {errors.zip_code && (
                      <p className="text-sm text-red-600 mt-1">{errors.zip_code}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="primary_phone" className="text-sm font-semibold text-gray-700">
                      Phone Number *
                    </Label>
                    <Input
                      id="primary_phone"
                      value={formData.primary_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, primary_phone: e.target.value }))}
                      className="mt-1"
                      placeholder="(555) 123-4567"
                    />
                    {errors.primary_phone && (
                      <p className="text-sm text-red-600 mt-1">{errors.primary_phone}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="primary_email" className="text-sm font-semibold text-gray-700">
                      Email Address
                    </Label>
                    <Input
                      id="primary_email"
                      type="email"
                      value={formData.primary_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, primary_email: e.target.value }))}
                      className="mt-1"
                      placeholder="email@example.com"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label htmlFor="website" className="text-sm font-semibold text-gray-700">
                      Website
                    </Label>
                    <Input
                      id="website"
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                      className="mt-1"
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Services Offered */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-indigo-600" />
                  <span>Services Offered *</span>
                </h3>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {AVAILABLE_SERVICES.map(service => (
                    <label
                      key={service}
                      className="flex items-center space-x-2 p-3 border-2 border-gray-200 rounded-lg hover:border-indigo-400 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.services.includes(service)}
                        onChange={() => handleServiceToggle(service)}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">{service}</span>
                    </label>
                  ))}
                </div>
                {errors.services && (
                  <p className="text-sm text-red-600">{errors.services}</p>
                )}
              </div>

              {/* Insurance and Payment */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>Insurance & Payment</span>
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-green-400 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.accepts_medicaid}
                      onChange={(e) => setFormData(prev => ({ ...prev, accepts_medicaid: e.target.checked }))}
                      className="w-5 h-5 text-green-600 rounded"
                    />
                    <span className="text-base font-semibold text-gray-800">Accepts Medicaid</span>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-green-400 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.accepts_medicare}
                      onChange={(e) => setFormData(prev => ({ ...prev, accepts_medicare: e.target.checked }))}
                      className="w-5 h-5 text-green-600 rounded"
                    />
                    <span className="text-base font-semibold text-gray-800">Accepts Medicare</span>
                  </label>
                </div>
              </div>

              {/* Languages */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                  <Languages className="w-5 h-5 text-purple-600" />
                  <span>Languages Spoken *</span>
                </h3>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {LANGUAGES.map(language => (
                    <label
                      key={language}
                      className="flex items-center space-x-2 p-3 border-2 border-gray-200 rounded-lg hover:border-purple-400 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.languages_spoken.includes(language)}
                        onChange={() => handleLanguageToggle(language)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">{language}</span>
                    </label>
                  ))}
                </div>
                {errors.languages_spoken && (
                  <p className="text-sm text-red-600">{errors.languages_spoken}</p>
                )}
              </div>

              {/* Accessibility */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                  <Accessibility className="w-5 h-5 text-blue-600" />
                  <span>Accessibility</span>
                </h3>
                <label className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.wheelchair_accessible}
                    onChange={(e) => setFormData(prev => ({ ...prev, wheelchair_accessible: e.target.checked }))}
                    className="w-5 h-5 text-blue-600 rounded"
                  />
                  <span className="text-base font-semibold text-gray-800">Wheelchair Accessible</span>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="px-6 py-2.5 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-2.5 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Provider
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
